import type { Promisable } from "type-fest";

/** The parts of a window this module touches, so tests can hand in a stand-in. */
export type ConnectTab = Pick<Window, "opener" | "close"> & {
  location: Pick<Location, "href">;
};

export type OpenWindow = (url: string, target: string) => ConnectTab | null;

const openBrowserWindow: OpenWindow = (url, target) => window.open(url, target);

export type LoadUrlHere = (url: string) => void;

const loadInThisWindow: LoadUrlHere = (url) => {
  window.location.assign(url);
};

/**
 * Sends the doctor to a calendar provider's consent page in a new tab.
 *
 * The tab is opened before the authorisation URL is requested: a browser only
 * lets a page open a window synchronously inside the user's click, so awaiting
 * the URL first would have popup blockers swallow it. The blank tab is pointed
 * at the URL once it arrives. Where the browser refuses the tab, the consent
 * page loads in this one instead.
 *
 * The new tab is disowned so the provider's page holds no handle on this one.
 * That does not stop `window.close()` working over there: whether a tab may
 * close itself is fixed when script creates it, not by who it can still see.
 */
export async function connectInNewTab(
  fetchAuthUrl: () => Promisable<string>,
  open: OpenWindow = openBrowserWindow,
  loadHere: LoadUrlHere = loadInThisWindow,
): Promise<void> {
  const tab = open("", "_blank");
  if (tab) {
    tab.opener = null;
  }

  let authUrl: string;
  try {
    authUrl = await fetchAuthUrl();
  } catch (error) {
    tab?.close();
    throw error;
  }

  if (tab) {
    tab.location.href = authUrl;
  } else {
    loadHere(authUrl);
  }
}
